import { Navigate } from "react-router-dom";
import { useRBAC } from "./hooks";
import { FOOD_NAV, TUDO_SOCIAL_NAV } from "./modules";

/**
 * Ordered list of "best-landing" paths for a freshly-authenticated or
 * permission-denied user. First entry whose gate permission the user
 * holds wins. The final fallback is ``/profile`` -- every admin user
 * who passed the login gate has *something* so we should almost never
 * reach that, but it's there so we don't render a blank page if the
 * RBAC payload is somehow empty.
 *
 * Order is deliberate:
 *   1. ``dashboard.view``  -> support admins / IP owner (global overview)
 *   2. ``ticket.transfer`` -> support-tier users (their primary surface)
 *   3. ``ticket.view``     -> dept leads / members (work queue hub)
 *   4. ``rbac.manage``     -> RBAC-only admins (rare, e.g. auditors)
 *   5. ``permission.view`` -> catalogue-only auditors
 *
 * Add new entries here when a new role-type needs a distinct landing.
 */
const LANDING_PREFERENCES: Array<{ permission: string; path: string }> = [
  { permission: "dashboard.view", path: "/dashboard" },
  { permission: "ticket.transfer", path: "/support" },
  { permission: "ticket.view", path: "/departments" },
  { permission: "rbac.manage", path: "/rbac" },
  { permission: "permission.view", path: "/rbac/permissions" },
  // Module roles hold none of the gates above -- a Food Onboarding Officer
  // would land on /profile, a blank-feeling first screen for 8 brand new
  // roles. Sourced from the module nav so it cannot drift from the routes.
  ...FOOD_NAV.map((entry) => ({ permission: entry.permission, path: entry.path })),
  // Same reasoning for Tudo Social: its 5 roles hold none of the gates
  // above, so an editor or moderator was landing on /profile.
  ...TUDO_SOCIAL_NAV.map((entry) => ({ permission: entry.permission, path: entry.path })),
];

const FINAL_FALLBACK = "/profile";

export function getLandingPath(
  hasPermission: (codename: string) => boolean,
): string {
  for (const pref of LANDING_PREFERENCES) {
    if (hasPermission(pref.permission)) return pref.path;
  }
  return FINAL_FALLBACK;
}

/**
 * React Router ``<Navigate>`` that computes its destination from the
 * current user's permissions. Use this anywhere that today hardcodes
 * ``/dashboard`` as the redirect target (post-login, root index,
 * wildcard 404 catch, ProtectedRoute fallback).
 */
export function LandingRedirect() {
  const { hasPermission, isLoading } = useRBAC();

  // Avoid redirecting while the RBAC payload is still loading -- we'd
  // compute the fallback path from an empty permission set and
  // immediately bounce the user somewhere wrong. A thin loader keeps
  // the browser stable for the split-second fetch.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return <Navigate to={getLandingPath(hasPermission)} replace />;
}
